import type { Client } from '../../api/client.js';
import type { AvancementEtape, ForcageLu } from './modele.js';

/**
 * Contrôleur de D17 (L64) : lit l'avancement du dossier (`demarrage.lire`), le dossier lui-même (`dossiers.lire`, pour le nommer en
 * tête) et ses mandats (`mandats.lister`, pour l'étape 21) ; marque une étape (`demarrage.marquer` : faite, sans objet avec motif, ou
 * rouverte) et enregistre la réponse à sa question (`demarrage.repondre`). L'état affiché est toujours celui que le serveur rend
 * après l'écriture : rien n'est montré comme fait tant que l'enregistrement n'a pas réussi.
 */
export interface DossierNomme {
  titre: string;
  reference: string;
}

export interface EtatDemarrage {
  statut: 'chargement' | 'pret' | 'erreur';
  avancement: readonly AvancementEtape[];
  /** Forçages tracés du dossier (couloir G, point 5) : qui, quand, motif, conditions manquantes. */
  forcages: readonly ForcageLu[];
  /** Erreur de chargement (écran entier). */
  erreur: string | null;
  /** Dernière écriture refusée : l'étape concernée et le message, dit là où l'on a cliqué (U3). */
  erreurEcriture: { code: string; message: string } | null;
  /** Étape dont l'écriture est en cours (boutons désactivés). */
  enCours: string | null;
  dossier: DossierNomme | null;
  /** Vrai seulement si un mandat de recherche d'héritiers signé figure au dossier ; null si les mandats n'ont pas pu être lus. */
  mandatRechercheSigne: boolean | null;
  /** Nombre de personnes de l'arbre (condition « Au moins une personne dans l'arbre ») ; null si l'arbre n'a pas pu être lu. */
  personnesArbre: number | null;
  /** Nombre de personnes de l'arbre au rôle « auteur » (condition « Défunt renseigné ») ; null si l'arbre n'a pas pu être lu. */
  defuntsArbre: number | null;
  /** Date de signature du mandat de recherche (le plus récent), pour le rappeler à l'étape 21 (N1). */
  mandatRechercheSigneLe: string | null;
}

interface Vue {
  dossierId: string;
  etapes: AvancementEtape[];
  forcages?: ForcageLu[];
}
interface MandatLu {
  kind: string;
  status: string;
  signedAt: string | null;
}

export interface OptionsControleur {
  client: Pick<Client, 'appeler'>;
  dossierId: string;
  versionCatalogue: string;
}

/** Juge du 30/10 (U4) : messages vrais, pour l'équipe ; rien n'est gardé sur l'appareil, l'équipe ne se contacte pas elle-même. */
export const MESSAGE_ECRITURE = 'L’étape n’a pas été enregistrée. Vérifiez la connexion puis réessayez.';
export const MESSAGE_CHARGEMENT = 'L’avancement de ce dossier n’a pas pu être chargé. Vérifiez la connexion puis réessayez.';

const comptesArbre = (arbre: { nodes?: { role?: string }[] } | null) =>
  Array.isArray(arbre?.nodes)
    ? { personnesArbre: arbre.nodes.length, defuntsArbre: arbre.nodes.filter((n) => n.role === 'auteur').length }
    : { personnesArbre: null, defuntsArbre: null };

export const MESSAGE_DEFUNT = 'Le défunt n’a pas été enregistré. Vérifiez la connexion puis réessayez.';

const ouRepli = async <T>(promesse: Promise<T>, repli: T): Promise<T> => promesse.catch(() => repli);

export function creerControleurDemarrage({ client, dossierId, versionCatalogue }: OptionsControleur) {
  let etat: EtatDemarrage = {
    statut: 'chargement',
    avancement: [],
    forcages: [],
    erreur: null,
    erreurEcriture: null,
    enCours: null,
    dossier: null,
    mandatRechercheSigne: null,
    mandatRechercheSigneLe: null,
    personnesArbre: null,
    defuntsArbre: null,
  };
  const abonnes = new Set<() => void>();
  const changer = (suite: Partial<EtatDemarrage>) => {
    etat = { ...etat, ...suite };
    for (const f of abonnes) f();
  };
  const parametres = { dossierId };

  async function charger(): Promise<void> {
    changer({ statut: 'chargement', erreur: null });
    try {
      // Le nom du dossier et ses mandats sont accessoires : leur échec n'empêche jamais d'afficher l'avancement.
      const [vue, dossier, mandats, arbre] = await Promise.all([
        client.appeler<Vue>('demarrage.lire', { parametres }),
        ouRepli(client.appeler<{ title: string; reference: string }>('dossiers.lire', { parametres }), null),
        ouRepli(client.appeler<MandatLu[]>('mandats.lister', { parametres }), null),
        ouRepli(client.appeler<{ nodes?: { role?: string }[] }>('genealogie.arbre', { parametres }), null),
      ]);
      const signes = Array.isArray(mandats) ? mandats.filter((m) => m.kind === 'mandat_recherche' && m.status === 'signe') : null;
      const dates = (signes ?? []).map((m) => m.signedAt).filter((x): x is string => typeof x === 'string').sort();
      changer({
        statut: 'pret',
        avancement: vue.etapes,
        forcages: vue.forcages ?? [],
        dossier: dossier ? { titre: dossier.title, reference: dossier.reference } : null,
        mandatRechercheSigne: signes ? signes.length > 0 : null,
        mandatRechercheSigneLe: dates.at(-1) ?? null,
        ...comptesArbre(arbre),
      });
    } catch {
      changer({ statut: 'erreur', erreur: MESSAGE_CHARGEMENT });
    }
  }

  async function ecrire(codeEtape: string, routeId: string, corps: Record<string, unknown>): Promise<boolean> {
    changer({ enCours: codeEtape, erreurEcriture: null });
    try {
      const vue = await client.appeler<Vue>(routeId, { parametres: { dossierId, codeEtape }, corps: { ...corps, versionCatalogue } });
      changer({ avancement: vue.etapes, forcages: vue.forcages ?? etat.forcages, enCours: null });
      return true;
    } catch {
      changer({ enCours: null, erreurEcriture: { code: codeEtape, message: MESSAGE_ECRITURE } });
      return false;
    }
  }

  return {
    subscribe(f: () => void) {
      abonnes.add(f);
      return () => void abonnes.delete(f);
    },
    getEtat: () => etat,
    charger,
    reessayer: charger,
    marquerFait: (code: string) => ecrire(code, 'demarrage.marquer', { etat: 'fait' }),
    marquerSansObjet: (code: string, motif: string) => ecrire(code, 'demarrage.marquer', { etat: 'sans_objet', motif: motif.trim() }),
    rouvrir: (code: string) => ecrire(code, 'demarrage.marquer', { etat: null }),
    repondre: (code: string, reponse: string) => ecrire(code, 'demarrage.repondre', { reponse }),
    /** Point 4 : cocher ou décocher une condition de passage non évaluable. */
    cocher: (code: string, codeCondition: string, coche: boolean) => ecrire(code, 'demarrage.cocher', { codeCondition, coche }),
    /**
     * Forçage tracé d'une étape par la dirigeante (point 5). Quand le mandat manque, le forçage lève aussi le blocage de l'arbre
     * (portée « mandat_recherche ») : deux traces, une par blocage levé. L'étape devient faite ; l'état affiché est celui du serveur.
     */
    async forcer(code: string, conditions: readonly string[], motif: string, leverMandat: boolean): Promise<boolean> {
      changer({ enCours: code, erreurEcriture: null });
      try {
        if (leverMandat)
          await client.appeler('demarrage.forcer', { parametres, corps: { portee: 'mandat_recherche', conditions, motif } });
        const vue = await client.appeler<Vue>('demarrage.forcer', {
          parametres,
          corps: { portee: 'etape', codeEtape: code, versionCatalogue, conditions, motif },
        });
        changer({ avancement: vue.etapes, forcages: vue.forcages ?? etat.forcages, enCours: null });
        return true;
      } catch {
        changer({ enCours: null });
        return false;
      }
    },
    /**
     * Tournage du 30/10, défaut 2 : le défunt manquant se saisit depuis le guide. Créé au rôle « auteur », décédé, par la route de la
     * généalogie (exempté du mandat côté serveur), puis l'arbre est relu pour réévaluer « Défunt renseigné ».
     */
    async saisirDefunt(prenoms: string, nom: string): Promise<boolean> {
      if (nom.trim() === '') return false;
      changer({ enCours: 'defunt', erreurEcriture: null });
      try {
        await client.appeler('genealogie.personnes.creer', {
          parametres,
          corps: { firstNames: prenoms.trim(), lastName: nom.trim(), vitalStatus: 'decede', isHypothesis: false, role: 'auteur', deathDate: null },
        });
        const arbre = await ouRepli(client.appeler<{ nodes?: { role?: string }[] }>('genealogie.arbre', { parametres }), null);
        changer({ ...comptesArbre(arbre), enCours: null });
        return true;
      } catch {
        changer({ enCours: null, erreurEcriture: { code: 'defunt', message: MESSAGE_DEFUNT } });
        return false;
      }
    },
    /** Demande de déblocage par l'assistante : une tâche pour la dirigeante. */
    async demanderDeblocage(code: string, conditions: readonly string[]): Promise<boolean> {
      try {
        await client.appeler('demarrage.demanderDeblocage', { parametres, corps: { portee: 'etape', codeEtape: code, conditions } });
        return true;
      } catch {
        return false;
      }
    },
  };
}

export type ControleurDemarrage = ReturnType<typeof creerControleurDemarrage>;
